/**
 * QuickAnswer
 *
 * A short, citable answer block rendered directly under a page H1.
 * Designed for AEO: a single visible paragraph that answers the page's
 * core question in 1–2 sentences, marked up with `data-quick-answer`
 * so it can be targeted by Speakable JSON-LD and identified by SEO QA.
 *
 * Use sparingly — exactly one per page, right after the H1.
 */

import { cn } from "@/lib/utils";
import { getAnswer } from "@/lib/aeo/answers";

interface Props {
  /** Either an answer id from the answer library, or inline text. */
  answerId?: string;
  text?: string;
  /** Optional eyebrow label (e.g. "Kort svar"). */
  label?: string;
  className?: string;
  /** Tone matches the page section's accent. */
  tone?: "mint" | "peach" | "butter" | "neutral";
}

const toneStyles: Record<NonNullable<Props["tone"]>, string> = {
  mint: "border-l-mint/80",
  peach: "border-l-peach/80",
  butter: "border-l-butter/80",
  neutral: "border-l-foreground/30",
};

export function QuickAnswer({
  answerId,
  text,
  label = "Kort svar",
  className,
  tone = "mint",
}: Props) {
  const resolved = answerId ? getAnswer(answerId)?.shortAnswer : text;
  if (!resolved) return null;

  return (
    <aside
      data-quick-answer
      data-aeo="quick-answer"
      className={cn(
        "mt-6 max-w-3xl border-l-4 bg-foreground/[0.03] py-4 pl-5 pr-4",
        "text-balance text-base leading-relaxed text-foreground/90 md:text-lg",
        toneStyles[tone],
        className,
      )}
    >
      <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.18em] text-foreground/60">
        {label}
      </p>
      <p className="m-0">{resolved}</p>
    </aside>
  );
}
